import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Typography,
    Box,
    Chip,
    CircularProgress,
    Divider
} from '@mui/material';
import {
    Close as CloseIcon,
    Restore as RestoreIcon,
    Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useDataProvider, useNotify } from 'react-admin';

interface WikiVersion {
    id: number;
    versionNumber: number;
    content: string;
    changeComment?: string;
    createdBy: number;
    createdAt: string;
}

interface WikiVersionHistoryProps {
    open: boolean;
    onClose: () => void;
    articleId: number;
    currentVersionId?: number;
    onRestore: (versionId: number, versionNumber: number, content: string) => void;
}

const WikiVersionHistory: React.FC<WikiVersionHistoryProps> = ({
    open,
    onClose,
    articleId,
    currentVersionId,
    onRestore
}) => {
    const [versions, setVersions] = useState<WikiVersion[]>([]);
    const [loading, setLoading] = useState(false);
    const [previewVersion, setPreviewVersion] = useState<WikiVersion | null>(null);

    const dataProvider = useDataProvider();
    const notify = useNotify();

    useEffect(() => {
        if (open && articleId) {
            loadVersions();
        }
    }, [open, articleId]);

    const loadVersions = async () => {
        setLoading(true);
        try {
            // Filter by article IRI
            const { data } = await dataProvider.getList('wiki_versions', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'versionNumber', order: 'DESC' },
                filter: { article: `/api/wiki_articles/${articleId}` }
            });
            setVersions(data);
        } catch (error) {
            notify('Fehler beim Laden der Versionen', { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleRestore = (version: WikiVersion) => {
        if (window.confirm(`Möchten Sie wirklich Version ${version.versionNumber} wiederherstellen?`)) {
            onRestore(version.id, version.versionNumber, version.content);
            onClose();
        }
    };

    const handlePreview = (version: WikiVersion) => {
        setPreviewVersion(version);
    };

    const handleClosePreview = () => {
        setPreviewVersion(null);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleString('de-DE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        Versionsverlauf
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : versions.length === 0 ? (
                        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                            Keine Versionen vorhanden
                        </Typography>
                    ) : (
                        <List>
                            {versions.map((version, index) => (
                                <React.Fragment key={version.id}>
                                    <ListItem>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="subtitle1">
                                                        Version {version.versionNumber}
                                                    </Typography>
                                                    {version.id === currentVersionId && (
                                                        <Chip label="Aktuell" color="primary" size="small" />
                                                    )}
                                                </Box>
                                            }
                                            secondary={
                                                <Box sx={{ mt: 0.5 }}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {formatDate(version.createdAt)}
                                                    </Typography>
                                                    {version.changeComment && (
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                            {version.changeComment}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            }
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton
                                                edge="end"
                                                onClick={() => handlePreview(version)}
                                                sx={{ mr: 1 }}
                                            >
                                                <VisibilityIcon />
                                            </IconButton>
                                            {version.id !== currentVersionId && (
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleRestore(version)}
                                                    color="primary"
                                                >
                                                    <RestoreIcon />
                                                </IconButton>
                                            )}
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                    {index < versions.length - 1 && <Divider />}
                                </React.Fragment>
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Schließen</Button>
                </DialogActions>
            </Dialog>

            {/* Preview Dialog */}
            <Dialog
                open={!!previewVersion}
                onClose={handleClosePreview}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        Version {previewVersion?.versionNumber} - Vorschau
                        <IconButton onClick={handleClosePreview} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {previewVersion && (
                        <Box
                            sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                '& h1': { fontSize: '2em', fontWeight: 'bold', marginTop: '0.67em', marginBottom: '0.67em' },
                                '& h2': { fontSize: '1.5em', fontWeight: 'bold', marginTop: '0.75em', marginBottom: '0.75em' },
                                '& h3': { fontSize: '1.17em', fontWeight: 'bold', marginTop: '0.83em', marginBottom: '0.83em' },
                                '& p': { marginTop: '1em', marginBottom: '1em' },
                                '& ul, & ol': { paddingLeft: '2em', marginTop: '1em', marginBottom: '1em' },
                                '& code': { backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px', fontFamily: 'monospace' },
                                '& pre': { backgroundColor: '#f5f5f5', padding: '1em', borderRadius: '4px', overflow: 'auto' },
                            }}
                            dangerouslySetInnerHTML={{ __html: previewVersion.content }}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    {previewVersion && previewVersion.id !== currentVersionId && (
                        <Button
                            onClick={() => handleRestore(previewVersion)}
                            variant="contained"
                            startIcon={<RestoreIcon />}
                        >
                            Wiederherstellen
                        </Button>
                    )}
                    <Button onClick={handleClosePreview}>Schließen</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default WikiVersionHistory;
